/* global AppSettings */
/**
 * Store details in storefinder bar overlay
 *
 * @author  Tamara Bazko
 */

import $ from 'jquery';
import Mustache from 'mustache';
import Factory from 'components/utils/Factory';
import EventTypes from 'components/EventTypes';
import createLogger from 'components/logger/Logger';
import matchBreakpoint from 'components/utils/matchBreakpoint';
import device from 'components/utils/device';
import scrollTo from 'components/utils/scrollTo';
import storeDetailsTemplate from './templates/storeDetails.html';
import storeOpeningTimeTemplate from './templates/storeOpeningTime.html';
import StoreFinderComponent from './StoreFinderComponent';
import 'jquery.extendPrototype';
import 'components/domutils/Element.jQuery';

const Logger = createLogger('StoreDetails');

const labels = window.labels?.storeFinder || {};
const chinaMagicLatOffset = 0.00575; // Magic numbers for fixing Baidu offset,
const chinaMagicLngOffset = 0.00668; // needed to provide correct directions on Baidu map (via link).
// Using Baidu in this case, because:
// 1) It's most popular map in China
// 2) Sogou doesn't provide any URI scheme anyway
function StoreDetails($element, settings) {
  Logger.info('Created');

  settings = settings || {};
  settings.selector = $element;

  StoreFinderComponent.call(this, settings, module);

  this.$storeDetails = this.$element;
  this.$stockProductTitle = this.$storeFinder.find(this.settings.stockProductTitleSelector);
  this.bindEvents();
}
$.extendPrototype(StoreDetails, StoreFinderComponent.prototype, {
  $storeDetails: undefined,
  $directionsTrigger: undefined,
  $storeDetailsTrigger: undefined,
  $stockProductTitle: undefined,
  currentStore: undefined,
  currentStoreAllData: undefined,

  settings: {
    selector: '.js-storeFinder-details',
    storeDetailsSelector: '.js-storeFinder-details',
    directionsTriggerSelector: '.js-storeFinder-directions-openTrigger',
    stockProductTitleSelector: '.js-stock-product-title',
  },

  /**
   * Bind events
   */
  bindEvents() {
    document.jq
      .on(EventTypes.STOREFINDER_GET_USER_LOCATION, this.onGetUserLocation.bind(this))
      .on(EventTypes.STOREFINDER_GET_USER_LOCATION_ERROR, this.onGetUserLocationError.bind(this))
      .on(
        EventTypes.STOREFINDER_STORE_DETAILS_OPEN_REQUEST,
        this.openStoreDetailsRequest.bind(this)
      )
      .on(EventTypes.STOREFINDER_STORE_DETAILS_CLOSE_REQUEST, this.closeStoreDetails.bind(this))
      .on(EventTypes.DIRECTIONS_HIDE_OVERLAY, this.onDirectionsHide.bind(this));
  },

  /**
   * Hide (not close) store details overlay
   */
  onDirectionsHide() {
    this.$storeDetails.removeClass('is-hidden');
  },

  /**
   * Check if user location is real one, form GPS (not search coords)
   * Check if it wasn't set to false before (relevant for China),
   * because location can be outside China, that triggers error event,
   * however the location still can be picked up
   *
   * @param  {object} event     jQuery event object
   * @param  {object} eventData   Data
   */
  onGetUserLocation(event, eventData) {
    if (eventData.coordinates && eventData.fromGPS && this.userLocation !== false) {
      this.userLocation = eventData.coordinates;
    }
  },

  /**
   * Neccessary in case when user provided user location,
   * but is outside China
   */
  onGetUserLocationError() {
    this.userLocation = false;
  },

  /**
   * Set rules for Directions button, differ per device and for China
   */
  setDirectionsTrigger() {
    const $directionsTrigger = this.$storeDetails.find(this.settings.directionsTriggerSelector);

    $directionsTrigger.on(
      'click',
      function (e) {
        const lat = $directionsTrigger.data('lat');
        const lng = $directionsTrigger.data('lng');
        const label = $directionsTrigger.data('label');
        let win;

        if (AppSettings.country.toLowerCase() !== 'cn') {
          e.preventDefault();

          this.$storeDetails.addClass('is-hidden');
          this.$storeDetails.trigger(EventTypes.DIRECTIONS_SHOW_OVERLAY, this.currentStore);

          if (!matchBreakpoint('xsmall')) {
            if (device.osName === device.IOS) {
              // eslint-disable-next-line no-alert
              if (window.confirm(`${labels.openWith} Maps?`)) {
                win = window.open(
                  `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d&t=m`,
                  '_self'
                );
                win.opener = null;
              }
            } else if (device.osName === device.ANDROID) {
              win = window.open(`geo:0,0?q=${lat},${lng} (${label})`);
              win.opener = null;
            }
          }
        } else {
          const href = $directionsTrigger.attr('data-href');
          e.preventDefault();

          // eslint-disable-next-line no-alert
          if (window.confirm(labels.openInBaidu)) {
            win = window.open(href, '_blank');
            win.opener = null;
          }
        }
      }.bind(this)
    );
  },

  /**
   * On request opens store details and check the country
   *
   * @param  {object} event       jQuery event object
   * @param  {object} eventData   Data belonging to event
   */
  openStoreDetailsRequest(event, eventData) {
    const store = eventData.store || eventData.marker.store;
    this.currentStore = {
      lat: store.latitude,
      lng: store.longitude,
      sogoulat: store.sogoulatitude,
      sogoulng: store.sogoulongitude,
      name: store.name,
      findAStoreSpecificInformation: store.findAStoreSpecificInformation,
    };
    this.currentStoreAllData = eventData;
    this.openStoreDetails(event, eventData);
  },

  /**
   * Open store details (on the place of storefinder bar)
   * @param  {object} event     jQuery event object
   * @param  {object} eventData   Data belonging to event
   */
  openStoreDetails(event, eventData) {
    this.$storeDetails.empty();

    if (eventData.fromList) {
      this.$storeDetails.trigger(EventTypes.STOREFINDER_STORE_DETAILS_OPEN, eventData);
    }

    this.$storeDetails.trigger(EventTypes.STOREFINDER_HIDE_LIST_REQUEST);

    if (!matchBreakpoint(this.settings.breakpointOverlay)) {
      $('.js-topNavigation').removeClass('is-hidden');
      this.$storeDetails.trigger(EventTypes.TOPNAV_SHOW);
      scrollTo(0, 0);
    }

    this.$storeDetails.removeClass('is-hidden');

    const store = eventData.store || eventData.marker.store;
    const storeData = {
      storeName: store.name,
      storeAdress: store.address,
      storeZipIndex: store.zip_code,
      storeCity: store.city,
      storeDistance: store.distanceFormatted,
      store,
      cn_lat: parseFloat(store.latitude) + chinaMagicLatOffset,
      cn_lng: parseFloat(store.longitude) + chinaMagicLngOffset,
      storeTelephoneNumber: store.telephone_number,
      findAStoreSpecificInformation: store.findAStoreSpecificInformation,
      labels,
    };

    // Product title should be present on PDP stock locator and used as main title on
    // store details overlay
    if (this.$stockProductTitle.length) {
      storeData.stockProductTitle = this.$stockProductTitle.text();
    }

    const openingTime = {
      monday: store.monday,
      tuesday: store.tuesday,
      wednesday: store.wednesday,
      thursday: store.thursday,
      friday: store.friday,
      saturday: store.saturday,
      sunday: store.sunday,
    };

    let openingTimeValues = [];

    for (const key in openingTime) {
      openingTimeValues = openingTime[key];
      if (key === store.todayIs) {
        const newPropertyName = `${key}IsActive`;
        openingTime[newPropertyName] = true;
      }
      if (openingTimeValues && openingTimeValues.length) {
        openingTime.visible = true;
      }
    }

    if (this.userLocation) {
      storeData.userLocation = this.userLocation;
      storeData.cn_userLat = parseFloat(this.userLocation.latitude) + chinaMagicLatOffset;
      storeData.cn_userLng = parseFloat(this.userLocation.longitude) + chinaMagicLngOffset;
    } else {
      storeData.noUserLocation = true;
    }

    openingTime.labels = storeData.labels;
    openingTime.temporaryClosedMessage = store.temporaryClosedMessage;
    const storeOpeningTimeContent = Mustache.render(storeOpeningTimeTemplate, openingTime);
    storeData.storeOpeningTime = storeOpeningTimeContent;
    const storeInfoWindowContent = Mustache.render(storeDetailsTemplate, storeData);

    $(storeInfoWindowContent).appendTo('.js-storeFinder-details');
    $(storeOpeningTimeContent).appendTo(this.$storeFinder);

    this.$storeDetailsTrigger = $('.js-storeFinder-details-trigger');
    this.$storeDetailsTrigger.on('vclick.closeStoreDetails', this.onDetailsTriggerClick.bind(this));
    this.$storeDetailsTrigger.on('keypress', ev => {
      if (ev.which === 13) {
        this.$storeDetailsTrigger.click();
      }
    });
    this.setDirectionsTrigger();
  },

  /**
   * Close details overlay
   */
  onDetailsTriggerClick(event) {
    this.currentStoreAllData.fromMap = false;
    this.closeStoreDetails(event, this.currentStoreAllData);
  },

  /**
   * Close store details (on the place of storefinder bar)
   * @param  {object} event     jQuery event object
   * @param  {object} eventData   Data belonging to event
   */
  closeStoreDetails(event, eventData) {
    this.$storeDetails.addClass('is-hidden').empty();
    $('.js-storeFinder-details-list').detach();
    this.$storeDetails.trigger(EventTypes.STOREFINDER_SHOW_LIST_REQUEST);
    if (eventData.fromMap === false) {
      eventData.fromMap = undefined;
      eventData.fromList = true;
      this.$storeDetails.trigger(EventTypes.STOREFINDER_STORE_DETAILS_CLOSE, eventData);
    }

    if (!matchBreakpoint(this.settings.breakpointOverlay)) {
      this.$element.trigger(EventTypes.STOREFINDER_HIDE_MAP_REQUEST);
    }
  },
});

// Create a factory for this component, factories are used via `Factory.attachTo(selector, settings)`.
export default Factory.create(StoreDetails);
